import { z } from 'zod';
import { decimalSchema } from './prisma-zod';
import { CustomFieldValueSchema } from './custom-field.types';

export enum InventoryStatus {
  IN_STOCK = 'IN_STOCK',
  OUT_OF_STOCK = 'OUT_OF_STOCK',
  RESERVED = 'RESERVED',
  DAMAGED = 'DAMAGED',
  DISCONTINUED = 'DISCONTINUED',
}

export enum InventoryCategoryType {
  PRODUCT = 'PRODUCT',
  MATERIAL = 'MATERIAL',
  EQUIPMENT = 'EQUIPMENT',
  SERVICE = 'SERVICE',
}

export enum InventoryItemType {
  INVENTORY = 'INVENTORY',
  SERVICE = 'SERVICE',
  NON_INVENTORY = 'NON_INVENTORY',
}

// Filter interface for inventory items
export interface InventoryItemFilters {
  search?: string;
  status?: InventoryStatus;
  categoryId?: string;
  locationId?: string;
  lowStock?: boolean;
  sortBy?: InventorySortField;
  sortOrder?: SortOrder;
  itemType?: InventoryItemType;
  isActive?: string | boolean;
}

export enum InventoryMovementType {
  IN = 'IN',
  OUT = 'OUT',
  TRANSFER = 'TRANSFER',
  ADJUSTMENT = 'ADJUSTMENT',
}

export enum InventoryMovementReason {
  PURCHASE = 'PURCHASE',
  SALE = 'SALE',
  RETURN = 'RETURN',
  DAMAGE = 'DAMAGE',
  THEFT = 'THEFT',
  TRANSFER = 'TRANSFER',
  CYCLE_COUNT = 'CYCLE_COUNT',
  CORRECTION = 'CORRECTION',
}

export enum InventoryLocationType {
  WAREHOUSE = 'WAREHOUSE',
  STORE = 'STORE',
  OFFICE = 'OFFICE',
  VEHICLE = 'VEHICLE',
}

export enum InventorySerialStatus {
  AVAILABLE = 'AVAILABLE',
  ALLOCATED = 'ALLOCATED',
  SOLD = 'SOLD',
  DAMAGED = 'DAMAGED',
}

export enum InventorySortField {
  NAME = 'name',
  SKU = 'sku',
  CATEGORY = 'category',
  STATUS = 'status',
  QUANTITY = 'quantity',
  CREATED_AT = 'createdAt',
  UPDATED_AT = 'updatedAt',
}

export type SortOrder = 'asc' | 'desc';

// Inventory Movement Reason — per-org lookup that replaces the static
// `InventoryMovementReason` enum for the values shown in the Reason
// dropdown on the RecordMovementDialog. Movements still store the chosen
// value as a free-form string code on `InventoryMovement.reason`, so this
// table is just allowed options + display metadata + which movement types
// each reason applies to.
export const InventoryMovementReasonOptionSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  name: z.string(),
  code: z.string(),
  description: z.string().optional().nullable(),
  color: z.string().optional().nullable(),
  icon: z.string().optional().nullable(),
  // Array of movement-type enum values (IN / OUT / TRANSFER / ADJUSTMENT)
  // this reason can be picked for. Empty means it shows for every type.
  applicableTypes: z.array(z.nativeEnum(InventoryMovementType)).default([]),
  isActive: z.boolean().default(true),
  sortOrder: z.number().int().default(0),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type InventoryMovementReasonOption = z.infer<
  typeof InventoryMovementReasonOptionSchema
>;

export const CreateInventoryMovementReasonOptionSchema =
  InventoryMovementReasonOptionSchema.omit({
    id: true,
    organizationId: true,
    createdAt: true,
    updatedAt: true,
  });
export type CreateInventoryMovementReasonOption = z.infer<
  typeof CreateInventoryMovementReasonOptionSchema
>;

export const UpdateInventoryMovementReasonOptionSchema =
  CreateInventoryMovementReasonOptionSchema.partial();
export type UpdateInventoryMovementReasonOption = z.infer<
  typeof UpdateInventoryMovementReasonOptionSchema
>;

// Inventory Category Type Option — per-org lookup that replaces the static
// `InventoryCategoryType` enum for the values shown in the Category form.
// Existing categories still store the chosen value as a free-form string
// code on `InventoryCategory.type`; this table is just the source of
// allowed options + display metadata.
export const InventoryCategoryTypeOptionSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  name: z.string(),
  code: z.string(),
  description: z.string().optional().nullable(),
  color: z.string().optional().nullable(),
  icon: z.string().optional().nullable(),
  isActive: z.boolean().default(true),
  sortOrder: z.number().int().default(0),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type InventoryCategoryTypeOption = z.infer<
  typeof InventoryCategoryTypeOptionSchema
>;

export const CreateInventoryCategoryTypeOptionSchema =
  InventoryCategoryTypeOptionSchema.omit({
    id: true,
    organizationId: true,
    createdAt: true,
    updatedAt: true,
  });
export type CreateInventoryCategoryTypeOption = z.infer<
  typeof CreateInventoryCategoryTypeOptionSchema
>;

export const UpdateInventoryCategoryTypeOptionSchema =
  CreateInventoryCategoryTypeOptionSchema.partial();
export type UpdateInventoryCategoryTypeOption = z.infer<
  typeof UpdateInventoryCategoryTypeOptionSchema
>;

// Inventory Category Schema
export const InventoryCategorySchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  name: z.string(),
  code: z.string(),
  description: z.string().optional().nullable(),
  color: z.string().optional().nullable(),
  icon: z.string().optional().nullable(),
  // Free-form code matching an `InventoryCategoryTypeOption.code` for this
  // org. The legacy `InventoryCategoryType` enum is kept as a seed source
  // only — callers should treat this as an opaque string.
  type: z.string(),

  // Accounting Integration
  defaultIncomeAccountCode: z.string().optional().nullable(),
  defaultExpenseAccountCode: z.string().optional().nullable(),
  defaultAssetAccountCode: z.string().optional().nullable(),
  taxCategoryCode: z.string().optional().nullable(),
  
  fields: z.any().default([]),
  isActive: z.boolean().default(true),
  metadata: z.any().default({}),
  createdAt: z.date(),
  updatedAt: z.date(),
  _count: z.object({
    items: z.number().optional(),
  }).optional(),
});

export type InventoryCategory = z.infer<typeof InventoryCategorySchema>;

export const CreateInventoryCategorySchema = InventoryCategorySchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  _count: true,
});

export type CreateInventoryCategory = z.infer<typeof CreateInventoryCategorySchema>;

export const UpdateInventoryCategorySchema = CreateInventoryCategorySchema.partial();
export type UpdateInventoryCategory = z.infer<typeof UpdateInventoryCategorySchema>;

// Inventory Item Schema
export const InventoryItemSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  categoryId: z.string().uuid(),
  name: z.string(),
  sku: z.string(),
  
  // Item Type Management
  itemType: z.nativeEnum(InventoryItemType).default(InventoryItemType.INVENTORY),
  
  // Enhanced Descriptions
  description: z.string().optional().nullable(),
  salesDescription: z.string().optional().nullable(),
  purchaseDescription: z.string().optional().nullable(),
  
  status: z.nativeEnum(InventoryStatus).default(InventoryStatus.IN_STOCK),
  metadata: z.any().default({}),
  
  // Stock Management
  quantity: z.number().int().min(0).default(0),
  minQuantity: z.number().int().min(0).optional().nullable(),
  maxQuantity: z.number().int().min(0).optional().nullable(),
  reorderPoint: z.number().int().min(0).optional().nullable(),
  reorderQuantity: z.number().int().min(0).optional().nullable(),
  
  // Initial Inventory Tracking
  initialQuantity: z.number().int().min(0).optional().nullable(),
  initialQuantityDate: z.date().optional().nullable(),
  
  // Enhanced Pricing and Costs
  unitCost: decimalSchema.optional().nullable(),
  purchaseCost: decimalSchema.optional().nullable(),
  unitPrice: decimalSchema.optional().nullable(),
  averageCost: decimalSchema.optional().nullable(),
  lastCost: decimalSchema.optional().nullable(),
  
  // Accounting Integration
  incomeAccountCode: z.string().optional().nullable(),
  expenseAccountCode: z.string().optional().nullable(),
  assetAccountCode: z.string().optional().nullable(),
  
  // Tax Management
  salesTaxCategoryCode: z.string().optional().nullable(),
  purchaseTaxCategoryCode: z.string().optional().nullable(),
  isTaxable: z.boolean().default(true),
  
  // Physical Properties
  weight: decimalSchema.optional().nullable(),
  weightUnit: z.string().optional().nullable(),
  dimensions: z.any().optional().nullable(),
  packSize: z.number().int().min(1).optional().nullable(),
  
  // Tracking and Management
  isTracked: z.boolean().default(true),
  isSerialized: z.boolean().default(false),
  hasBatches: z.boolean().default(false),
  hasExpiration: z.boolean().default(false),
  isActive: z.boolean().default(true),
  
  // Location and Supplier
  defaultLocationId: z.string().uuid().optional().nullable(),
  preferredSupplierId: z.string().uuid().optional().nullable(),
  
  // Business Fields
  barcode: z.string().optional().nullable(),
  manufacturerPartNumber: z.string().optional().nullable(),
  tags: z.array(z.string()).default([]),
  notes: z.string().optional().nullable(),
  
  // External System Mapping
  externalId: z.string().optional().nullable(),
  externalSystem: z.string().optional().nullable(),
  lastSyncAt: z.date().optional().nullable(),
  
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable().optional(),
  category: InventoryCategorySchema.optional(),
  defaultLocation: z.any().optional(),
  preferredSupplier: z.any().optional(),
  customFieldValues: z.array(CustomFieldValueSchema).optional(),

  // Pre-signed URL for the primary image (first ordered image attached to
  // this item, taken from the resized thumbnail when available). Server
  // batches the signing across the page so list views don't waterfall.
  thumbnailUrl: z.string().nullable().optional(),
  // Full ordered image set, attached only on the item-detail response.
  images: z.array(z.object({
    id: z.string().uuid(),
    fileName: z.string().nullable(),
    contentType: z.string().nullable(),
    fileSize: z.number().nullable(),
    order: z.number(),
    url: z.string().nullable(),
    thumbnailUrl: z.string().nullable(),
    isPrimary: z.boolean(),
  })).optional(),

  _count: z.object({
    movements: z.number().optional(),
    serials: z.number().optional(),
    batches: z.number().optional(),
  }).optional(),
});

export const InventoryItemImageSchema = z.object({
  id: z.string().uuid(),
  fileName: z.string().nullable(),
  contentType: z.string().nullable(),
  fileSize: z.number().nullable(),
  order: z.number(),
  url: z.string().nullable(),
  thumbnailUrl: z.string().nullable(),
  isPrimary: z.boolean(),
});
export type InventoryItemImage = z.infer<typeof InventoryItemImageSchema>;

export type InventoryItem = z.infer<typeof InventoryItemSchema>;

export const CreateInventoryItemSchema = InventoryItemSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
  category: true,
  defaultLocation: true,
  preferredSupplier: true,
  customFieldValues: true,
  _count: true,
}).extend({
  customFields: z.record(z.string(), z.any()).optional(),
});

export type CreateInventoryItem = z.infer<typeof CreateInventoryItemSchema>;

export const UpdateInventoryItemSchema = CreateInventoryItemSchema.partial();
export type UpdateInventoryItem = z.infer<typeof UpdateInventoryItemSchema>;

// Inventory Location Schema
export const InventoryLocationSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  name: z.string(),
  code: z.string(),
  description: z.string().optional().nullable(),
  type: z.nativeEnum(InventoryLocationType),
  address: z.any().optional().nullable(),
  isActive: z.boolean().default(true),
  parentId: z.string().uuid().optional().nullable(),
  metadata: z.any().default({}),
  createdAt: z.date(),
  updatedAt: z.date(),
  parent: z.any().optional(),
  children: z.array(z.any()).optional(),
  _count: z.object({
    itemLocations: z.number().optional(),
    defaultItems: z.number().optional(),
  }).optional(),
});

export type InventoryLocation = z.infer<typeof InventoryLocationSchema>;

export const CreateInventoryLocationSchema = InventoryLocationSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  parent: true,
  children: true,
  _count: true,
}).extend({
  customFields: z.record(z.string(), z.any()).optional(),
});

export type CreateInventoryLocation = z.infer<typeof CreateInventoryLocationSchema>;

export const UpdateInventoryLocationSchema = CreateInventoryLocationSchema.partial();
export type UpdateInventoryLocation = z.infer<typeof UpdateInventoryLocationSchema>;

// Batch allocation used when recording a movement for a batch-tracked item.
// Sum of `quantity` across entries must equal the movement's absolute quantity.
export const BatchAllocationSchema = z.object({
  batchId: z.string().uuid(),
  quantity: z.number().int().positive(),
});
export type BatchAllocation = z.infer<typeof BatchAllocationSchema>;

// Inventory Movement Schema
export const InventoryMovementSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  itemId: z.string().uuid(),
  type: z.nativeEnum(InventoryMovementType),
  // Free-form code matching an `InventoryMovementReasonOption.code` for
  // this org. The legacy `InventoryMovementReason` enum is kept as a
  // seed/legacy-reference source; treat this as an opaque string.
  reason: z.string(),
  // Signed when type=ADJUSTMENT, unsigned magnitude otherwise.
  quantity: z.number().int(),
  quantityBefore: z.number().int().optional().nullable(),
  quantityAfter: z.number().int().optional().nullable(),
  unitCost: decimalSchema.optional().nullable(),
  fromLocationId: z.string().uuid().optional().nullable(),
  toLocationId: z.string().uuid().optional().nullable(),
  referenceType: z.string().optional().nullable(),
  referenceId: z.string().uuid().optional().nullable(),
  userId: z.string().uuid().optional().nullable(),
  approvedBy: z.string().uuid().optional().nullable(),
  approvedAt: z.date().optional().nullable(),
  notes: z.string().optional().nullable(),
  metadata: z.any().default({}),
  movementDate: z.date(),
  createdAt: z.date(),
  item: InventoryItemSchema.optional(),
  fromLocation: InventoryLocationSchema.optional(),
  toLocation: InventoryLocationSchema.optional(),
  user: z.any().optional(),
  approver: z.any().optional(),
});

export type InventoryMovement = z.infer<typeof InventoryMovementSchema>;

// Create DTO: signed delta allowed (and required to be nonzero) for ADJUSTMENT;
// positive magnitude required for IN/OUT/TRANSFER. The refine guards against
// accidental zero/negative quantities on non-adjustment movements.
export const CreateInventoryMovementSchema = InventoryMovementSchema.omit({
  id: true,
  createdAt: true,
  quantityBefore: true,
  quantityAfter: true,
  item: true,
  fromLocation: true,
  toLocation: true,
  user: true,
  approver: true,
})
  .extend({
    serialIds: z.array(z.string().uuid()).optional(),
    batchAllocations: z.array(BatchAllocationSchema).optional(),
  })
  .refine(
    (m) =>
      m.type === InventoryMovementType.ADJUSTMENT
        ? Number.isInteger(m.quantity) && m.quantity !== 0
        : m.quantity > 0,
    {
      message:
        'Quantity must be a non-zero integer for ADJUSTMENT, or positive for IN/OUT/TRANSFER',
      path: ['quantity'],
    },
  );

export type CreateInventoryMovement = z.infer<typeof CreateInventoryMovementSchema>;

export const UpdateInventoryMovementSchema = InventoryMovementSchema.omit({
  id: true,
  createdAt: true,
  quantityBefore: true,
  quantityAfter: true,
  item: true,
  fromLocation: true,
  toLocation: true,
  user: true,
  approver: true,
}).partial();
export type UpdateInventoryMovement = z.infer<typeof UpdateInventoryMovementSchema>;

// Inventory Adjustment — cycle counts, damage write-offs, corrections.
// Absolute count ("I physically have 95 units") rather than a signed delta;
// the server computes `adjustment = quantityAfter - quantityBefore` when
// applying and creates a linked ADJUSTMENT movement for the journal.
export enum InventoryAdjustmentReason {
  CYCLE_COUNT = 'CYCLE_COUNT',
  DAMAGE = 'DAMAGE',
  THEFT = 'THEFT',
  CORRECTION = 'CORRECTION',
}

export enum InventoryAdjustmentStatus {
  PENDING = 'PENDING',
  APPLIED = 'APPLIED',
  REJECTED = 'REJECTED',
}

export const InventoryAdjustmentSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  itemId: z.string().uuid(),
  reason: z.nativeEnum(InventoryAdjustmentReason),
  quantityBefore: z.number().int(),
  quantityAfter: z.number().int().min(0),
  adjustment: z.number().int(),
  unitCost: decimalSchema.optional().nullable(),
  locationId: z.string().uuid().optional().nullable(),
  status: z.nativeEnum(InventoryAdjustmentStatus).default(InventoryAdjustmentStatus.APPLIED),
  userId: z.string().uuid(),
  approvedBy: z.string().uuid().optional().nullable(),
  approvedAt: z.date().optional().nullable(),
  rejectedBy: z.string().uuid().optional().nullable(),
  rejectedAt: z.date().optional().nullable(),
  approverNotes: z.string().optional().nullable(),
  appliedAt: z.date().optional().nullable(),
  movementId: z.string().uuid().optional().nullable(),
  notes: z.string().optional().nullable(),
  metadata: z.any().default({}),
  adjustmentDate: z.date(),
  createdAt: z.date(),
  updatedAt: z.date(),
  item: InventoryItemSchema.optional(),
  location: InventoryLocationSchema.optional(),
  user: z.any().optional(),
  approver: z.any().optional(),
  rejector: z.any().optional(),
});

export type InventoryAdjustment = z.infer<typeof InventoryAdjustmentSchema>;

export const CreateInventoryAdjustmentSchema = z.object({
  itemId: z.string().uuid(),
  reason: z.nativeEnum(InventoryAdjustmentReason),
  // Absolute count the user physically observed (for CYCLE_COUNT) or the
  // final quantity after write-off (for DAMAGE/THEFT/CORRECTION).
  physicalCount: z.number().int().min(0),
  locationId: z.string().uuid().optional().nullable(),
  unitCost: decimalSchema.optional().nullable(),
  notes: z.string().optional().nullable(),
  adjustmentDate: z.union([z.date(), z.string()]).optional(),
});
export type CreateInventoryAdjustment = z.infer<typeof CreateInventoryAdjustmentSchema>;

export const ApproveInventoryAdjustmentSchema = z.object({
  approverNotes: z.string().optional(),
});
export type ApproveInventoryAdjustment = z.infer<typeof ApproveInventoryAdjustmentSchema>;

export const RejectInventoryAdjustmentSchema = z.object({
  approverNotes: z.string().optional(),
});
export type RejectInventoryAdjustment = z.infer<typeof RejectInventoryAdjustmentSchema>;

export interface InventoryAdjustmentFilters {
  itemId?: string;
  status?: InventoryAdjustmentStatus;
  reason?: InventoryAdjustmentReason;
  locationId?: string;
  startDate?: string;
  endDate?: string;
}

// Inventory Serial Schema
export const InventorySerialSchema = z.object({
  id: z.string().uuid(),
  itemId: z.string().uuid(),
  serialNumber: z.string(),
  status: z.nativeEnum(InventorySerialStatus).default(InventorySerialStatus.AVAILABLE),
  locationId: z.string().uuid().optional().nullable(),
  purchaseDate: z.date().optional().nullable(),
  warrantyDate: z.date().optional().nullable(),
  notes: z.string().optional().nullable(),
  metadata: z.any().default({}),
  createdAt: z.date(),
  updatedAt: z.date(),
  item: InventoryItemSchema.optional(),
  location: InventoryLocationSchema.optional(),
});

export type InventorySerial = z.infer<typeof InventorySerialSchema>;

export const CreateInventorySerialSchema = InventorySerialSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  item: true,
  location: true,
});

export type CreateInventorySerial = z.infer<typeof CreateInventorySerialSchema>;

export const UpdateInventorySerialSchema = CreateInventorySerialSchema.partial();
export type UpdateInventorySerial = z.infer<typeof UpdateInventorySerialSchema>;

// Column Definitions
export const inventoryItemColumns = [
  { key: 'name', label: 'Name', sortable: true },
  { key: 'sku', label: 'SKU', sortable: true },
  { key: 'itemType', label: 'Type', sortable: true },
  { key: 'category', label: 'Category', sortable: true },
  { key: 'status', label: 'Status', sortable: true },
  { key: 'quantity', label: 'Quantity', sortable: true },
  { key: 'minQuantity', label: 'Min Qty', sortable: false },
  { key: 'unitCost', label: 'Unit Cost', sortable: false },
  { key: 'unitPrice', label: 'Unit Price', sortable: false },
  { key: 'defaultLocation', label: 'Location', sortable: false },
  { key: 'barcode', label: 'Barcode', sortable: false },
  { key: 'isActive', label: 'Active', sortable: false },
  { key: 'createdAt', label: 'Created', sortable: true },
  { key: 'updatedAt', label: 'Updated', sortable: true },
];

export const inventoryCategoryColumns = [
  { key: 'name', label: 'Name', sortable: true },
  { key: 'code', label: 'Code', sortable: true },
  { key: 'type', label: 'Type', sortable: true },
  { key: 'items', label: 'Items', sortable: false },
  { key: 'isActive', label: 'Active', sortable: false },
  { key: 'createdAt', label: 'Created', sortable: true },
  { key: 'updatedAt', label: 'Updated', sortable: true },
];

export const inventoryLocationColumns = [
  { key: 'name', label: 'Name', sortable: true },
  { key: 'code', label: 'Code', sortable: true },
  { key: 'type', label: 'Type', sortable: true },
  { key: 'parent', label: 'Parent Location', sortable: false },
  { key: 'isActive', label: 'Active', sortable: false },
  { key: 'createdAt', label: 'Created', sortable: true },
  { key: 'updatedAt', label: 'Updated', sortable: true },
];

export const inventoryMovementColumns = [
  { key: 'item', label: 'Item', sortable: false },
  { key: 'type', label: 'Type', sortable: true },
  { key: 'reason', label: 'Reason', sortable: true },
  { key: 'quantity', label: 'Quantity', sortable: true },
  { key: 'fromLocation', label: 'From', sortable: false },
  { key: 'toLocation', label: 'To', sortable: false },
  { key: 'user', label: 'User', sortable: false },
  { key: 'movementDate', label: 'Date', sortable: true },
  { key: 'createdAt', label: 'Created', sortable: true },
];
